import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import {
  User, MapPin, Briefcase, GraduationCap, Award, Camera,
  Plus, X, Loader2, CheckCircle2, Clock, AlertTriangle, Send
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SalesApplicationPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [appStatus, setAppStatus] = useState(null);
  const [existingApp, setExistingApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    full_name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    birthday: '',
    profile_photo_url: '',
    coverage_area: '',
    why_join: '',
  });

  const [employment, setEmployment] = useState([]);
  const [education, setEducation] = useState([]);
  const [certifications, setCertifications] = useState([]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!token) return;
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/sales/application`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAppStatus(res.data.status);
        if (res.data.application) {
          setExistingApp(res.data.application);
          const a = res.data.application;
          setForm({
            full_name: a.full_name || user?.name || '',
            email: a.email || user?.email || '',
            phone: a.phone || '',
            address: a.address || '',
            birthday: a.birthday || '',
            profile_photo_url: a.profile_photo_url || '',
            coverage_area: a.coverage_area || '',
            why_join: a.why_join || '',
          });
          if (a.employment_history?.length) setEmployment(a.employment_history);
          if (a.education_history?.length) setEducation(a.education_history);
          if (a.certifications?.length) setCertifications(a.certifications);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    checkStatus();
  }, [token]);

  useEffect(() => {
    if (user && !form.full_name) set('full_name', user.name || '');
    if (user && !form.email) set('email', user.email || '');
  }, [user]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      set('profile_photo_url', res.data.url);
      toast.success('Photo uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally { setUploading(false); }
  };

  const addEmployment = () => setEmployment(p => [...p, { company: '', title: '', start_date: '', end_date: '', description: '' }]);
  const updateEmployment = (i, k, v) => setEmployment(p => p.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const removeEmployment = (i) => setEmployment(p => p.filter((_, idx) => idx !== i));

  const addEducation = () => setEducation(p => [...p, { school: '', degree: '', field: '', start_date: '', end_date: '' }]);
  const updateEducation = (i, k, v) => setEducation(p => p.map((e, idx) => idx === i ? { ...e, [k]: v } : e));
  const removeEducation = (i) => setEducation(p => p.filter((_, idx) => idx !== i));

  const addCertification = () => setCertifications(p => [...p, { name: '', issuer: '', date_obtained: '' }]);
  const updateCertification = (i, k, v) => setCertifications(p => p.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const removeCertification = (i) => setCertifications(p => p.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!form.full_name || !form.email) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/sales/application`, {
        ...form,
        employment_history: employment,
        education_history: education,
        certifications: certifications,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Application submitted! We\'ll review it shortly.');
      setAppStatus('pending_review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-400" /></div>;

  // Status screens
  if (appStatus === 'pending_review') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="application-pending">
        <Card className="max-w-md w-full bg-card/80 border-border text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 rounded-full bg-amber-500/20 mx-auto mb-4 flex items-center justify-center">
              <Clock className="h-8 w-8 text-amber-400" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Application Under Review</h2>
            <p className="text-sm text-muted-foreground mb-4">Your application has been submitted and is being reviewed by our team. We'll notify you once a decision is made.</p>
            <Badge className="bg-amber-500/20 text-amber-400">Submitted {existingApp?.submitted_at ? new Date(existingApp.submitted_at).toLocaleDateString() : ''}</Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (appStatus === 'approved') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="application-approved">
        <Card className="max-w-md w-full bg-card/80 border-border text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Application Approved!</h2>
            <p className="text-sm text-muted-foreground mb-6">Welcome to the Eventism Sales Team. Complete your training to get started.</p>
            <Button onClick={() => navigate('/sales')} className="bg-indigo-600 hover:bg-indigo-700" data-testid="go-to-dashboard-btn">
              Go to Sales Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (appStatus === 'rejected') {
    const rejectedAt = existingApp?.rejected_at ? new Date(existingApp.rejected_at) : null;
    const canReapply = rejectedAt ? new Date() >= new Date(rejectedAt.getTime() + 30 * 24 * 60 * 60 * 1000) : true;
    const daysLeft = rejectedAt ? Math.max(0, Math.ceil((new Date(rejectedAt.getTime() + 30 * 24 * 60 * 60 * 1000) - new Date()) / (24 * 60 * 60 * 1000))) : 0;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="application-rejected">
        <Card className="max-w-md w-full bg-card/80 border-border text-center">
          <CardContent className="p-8">
            <div className="w-16 h-16 rounded-full bg-red-500/20 mx-auto mb-4 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Application Not Approved</h2>
            {existingApp?.rejection_reason && <p className="text-sm text-muted-foreground mb-2">Reason: {existingApp.rejection_reason}</p>}
            {canReapply ? (
              <Button onClick={() => { setAppStatus('none'); setExistingApp(null); }} className="bg-indigo-600 hover:bg-indigo-700 mt-4" data-testid="reapply-btn">
                Submit New Application
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground mt-4">You can re-apply in <span className="text-amber-400 font-bold">{daysLeft} days</span></p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Application Form
  return (
    <div className="min-h-screen bg-background pt-20" data-testid="sales-application-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-8">
        <div className="text-center mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground mb-2">Sales Agent Application</h1>
          <p className="text-sm text-muted-foreground">Complete your profile to join the Eventism sales team</p>
        </div>

        <div className="space-y-6">
          {/* Profile Photo + Basic Info */}
          <Card className="bg-muted/60 border-border" data-testid="profile-section">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-start gap-6">
                {/* Photo */}
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-28 h-28 rounded-full bg-muted border-2 border-border overflow-hidden flex items-center justify-center">
                      {form.profile_photo_url ? (
                        <img src={form.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-12 w-12 text-muted-foreground" />
                      )}
                    </div>
                    <button onClick={() => fileRef.current?.click()}
                      className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-indigo-600 border-2 border-border flex items-center justify-center hover:bg-indigo-700 transition-colors"
                      data-testid="upload-photo-btn">
                      {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground" /> : <Camera className="h-3.5 w-3.5 text-foreground" />}
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2">Upload photo</p>
                </div>

                {/* Basic Fields */}
                <div className="flex-1 w-full space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Full Name *</Label>
                      <Input value={form.full_name} onChange={e => set('full_name', e.target.value)}
                        className="bg-muted border-border h-9 text-sm" data-testid="app-name" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Email *</Label>
                      <Input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                        className="bg-muted border-border h-9 text-sm" data-testid="app-email" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Phone</Label>
                      <Input value={form.phone} onChange={e => set('phone', e.target.value)}
                        className="bg-muted border-border h-9 text-sm" data-testid="app-phone" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Birthday</Label>
                      <Input type="date" value={form.birthday} onChange={e => set('birthday', e.target.value)}
                        className="bg-muted border-border h-9 text-sm" data-testid="app-birthday" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Address</Label>
                    <Input value={form.address} onChange={e => set('address', e.target.value)}
                      placeholder="Full address" className="bg-muted border-border h-9 text-sm" data-testid="app-address" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Coverage Area + Why Join */}
          <Card className="bg-muted/60 border-border" data-testid="coverage-section">
            <CardContent className="p-6 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <MapPin className="h-4 w-4 text-indigo-400" />
                <h3 className="text-sm font-semibold text-foreground">Coverage & Motivation</h3>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Area You Want to Cover</Label>
                <Input value={form.coverage_area} onChange={e => set('coverage_area', e.target.value)}
                  placeholder="e.g. Denver Metro, Colorado Springs, Front Range" className="bg-muted border-border h-9 text-sm" data-testid="app-coverage" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Why Do You Want to Join Eventism?</Label>
                <Textarea value={form.why_join} onChange={e => set('why_join', e.target.value)}
                  placeholder="Tell us about your passion for the events industry and what drives you..."
                  className="bg-muted border-border text-sm" rows={3} data-testid="app-why-join" />
              </div>
            </CardContent>
          </Card>

          {/* Employment History */}
          <Card className="bg-muted/60 border-border" data-testid="employment-section">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-emerald-400" />
                  <h3 className="text-sm font-semibold text-foreground">Employment History</h3>
                </div>
                <Button size="sm" variant="outline" onClick={addEmployment} className="h-7 text-[10px] border-border text-muted-foreground" data-testid="add-employment-btn">
                  <Plus className="h-3 w-3 mr-0.5" /> Add
                </Button>
              </div>
              {employment.length === 0 && <p className="text-xs text-muted-foreground">No employment history added yet.</p>}
              <div className="space-y-4">
                {employment.map((emp, i) => (
                  <div key={i} className="p-3 rounded-lg border border-border bg-muted/30 relative" data-testid={`employment-entry-${i}`}>
                    <button onClick={() => removeEmployment(i)} className="absolute top-2 right-2 text-red-400 hover:text-red-300 p-1"><X className="h-3.5 w-3.5" /></button>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div><Label className="text-[10px] text-muted-foreground">Company</Label><Input value={emp.company} onChange={e => updateEmployment(i, 'company', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                      <div><Label className="text-[10px] text-muted-foreground">Job Title</Label><Input value={emp.title} onChange={e => updateEmployment(i, 'title', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div><Label className="text-[10px] text-muted-foreground">Start Date</Label><Input type="month" value={emp.start_date} onChange={e => updateEmployment(i, 'start_date', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                      <div><Label className="text-[10px] text-muted-foreground">End Date</Label><Input type="month" value={emp.end_date} onChange={e => updateEmployment(i, 'end_date', e.target.value)} placeholder="Present" className="bg-muted border-border h-8 text-xs" /></div>
                    </div>
                    <div><Label className="text-[10px] text-muted-foreground">Description</Label><Textarea value={emp.description} onChange={e => updateEmployment(i, 'description', e.target.value)} className="bg-muted border-border text-xs" rows={2} /></div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Education History */}
          <Card className="bg-muted/60 border-border" data-testid="education-section">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-blue-400" />
                  <h3 className="text-sm font-semibold text-foreground">Education</h3>
                </div>
                <Button size="sm" variant="outline" onClick={addEducation} className="h-7 text-[10px] border-border text-muted-foreground" data-testid="add-education-btn">
                  <Plus className="h-3 w-3 mr-0.5" /> Add
                </Button>
              </div>
              {education.length === 0 && <p className="text-xs text-muted-foreground">No education history added yet.</p>}
              <div className="space-y-4">
                {education.map((edu, i) => (
                  <div key={i} className="p-3 rounded-lg border border-border bg-muted/30 relative" data-testid={`education-entry-${i}`}>
                    <button onClick={() => removeEducation(i)} className="absolute top-2 right-2 text-red-400 hover:text-red-300 p-1"><X className="h-3.5 w-3.5" /></button>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div><Label className="text-[10px] text-muted-foreground">School</Label><Input value={edu.school} onChange={e => updateEducation(i, 'school', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                      <div><Label className="text-[10px] text-muted-foreground">Degree</Label><Input value={edu.degree} onChange={e => updateEducation(i, 'degree', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-[10px] text-muted-foreground">Field of Study</Label><Input value={edu.field} onChange={e => updateEducation(i, 'field', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                      <div><Label className="text-[10px] text-muted-foreground">Start</Label><Input type="month" value={edu.start_date} onChange={e => updateEducation(i, 'start_date', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                      <div><Label className="text-[10px] text-muted-foreground">End</Label><Input type="month" value={edu.end_date} onChange={e => updateEducation(i, 'end_date', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card className="bg-muted/60 border-border" data-testid="certifications-section">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-foreground">Certifications</h3>
                </div>
                <Button size="sm" variant="outline" onClick={addCertification} className="h-7 text-[10px] border-border text-muted-foreground" data-testid="add-cert-btn">
                  <Plus className="h-3 w-3 mr-0.5" /> Add
                </Button>
              </div>
              {certifications.length === 0 && <p className="text-xs text-muted-foreground">No certifications added yet.</p>}
              <div className="space-y-3">
                {certifications.map((cert, i) => (
                  <div key={i} className="p-3 rounded-lg border border-border bg-muted/30 relative" data-testid={`cert-entry-${i}`}>
                    <button onClick={() => removeCertification(i)} className="absolute top-2 right-2 text-red-400 hover:text-red-300 p-1"><X className="h-3.5 w-3.5" /></button>
                    <div className="grid grid-cols-3 gap-2">
                      <div><Label className="text-[10px] text-muted-foreground">Certification Name</Label><Input value={cert.name} onChange={e => updateCertification(i, 'name', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                      <div><Label className="text-[10px] text-muted-foreground">Issuing Organization</Label><Input value={cert.issuer} onChange={e => updateCertification(i, 'issuer', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                      <div><Label className="text-[10px] text-muted-foreground">Date</Label><Input type="month" value={cert.date_obtained} onChange={e => updateCertification(i, 'date_obtained', e.target.value)} className="bg-muted border-border h-8 text-xs" /></div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <Button onClick={handleSubmit} className="w-full bg-indigo-600 hover:bg-indigo-700 h-12 text-base font-semibold" disabled={submitting || !form.full_name || !form.email} data-testid="submit-application-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Submit Application
          </Button>
        </div>
      </div>
    </div>
  );
}
